import type { LoadableComponent } from 'react-loadable';

import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';

import { fg } from '@adminhub/feature-gating';
import { FEATURE_KEY_ADMINHUB_ORG_CREATE, isFeatureDisabled } from '@adminhub/feature-registry';
import { PickPageLoader } from '@adminhub/loading';
import { RouteNames } from '@adminhub/router';

// hint: Do not webpackPreload or  webpackPrefetch any page
// why: we have preloader

// hint: add /* webpackMode: "eager" */ to those roots, which shall not be split

// roots
const RequestGate = PickPageLoader(
  async () => import(/* webpackChunkName: "request-gate" */ '@adminhub-page/shadow-it'),
  ({ ShadowItRequestGate: Page }) => Page,
);

const ClaimApp = PickPageLoader(
  async () => import(/* webpackChunkName: "claim-app" */ '@adminhub-page/claim-app'),
  ({ ClaimApp: Page }) => Page,
);

const OrgPickerSwitcher = PickPageLoader(
  async () => import(/* webpackChunkName: "org-picker-switcher-app" */ '@adminhub-page/onboarding-agp'),
  ({ OrgPickerSwitcher: Page }) => Page,
);

const CreateOrganizationPage = PickPageLoader(
  async () => import(/* webpackChunkName: "create-org-app" */ '@adminhub-page/create-page'),
  ({ CreateOrganizationPage: Page }) => Page,
);

const Error404Page = PickPageLoader(
  async () => import(/* webpackMode: "eager" */ /* webpackChunkName: "error-404-page" */ '@adminhub/error-pages'),
  ({ Error404Page: Page }) => Page,
);

const OrgRoutes = PickPageLoader(
  async () => import(/* webpackMode: "eager" */ /* webpackChunkName: "org-app" */ '../routes/org-routes'),
  ({ OrgRoutes: Page }) => Page,
);

const SiteRoutes = PickPageLoader(
  async () => import(/* webpackMode: "eager" */ /* webpackChunkName: "site-app" */ '../routes/site-routes'),
  ({ SiteRoutes: Page }) => Page,
);

const Dashboard = PickPageLoader(
  async () => import(/* webpackMode: "eager" */ /* webpackChunkName: "dashboard-app" */ '@adminhub/dashboard'),
  ({ Dashboard: Page }) => Page,
);

const RovoSignupOrgPage = PickPageLoader(
  async () => import('@adminhub/rovo-signup'),
  ({ RovoSignupOrgPage: Page }) => Page,
);

const OrgOptIn = PickPageLoader(
  async () => import('@adminhub/migration-shared-experiences-org-opt-in'),
  ({ OrgOptIn: Page }) => Page,
);

const DspDeeplink = PickPageLoader(
  async () => import(/* webpackChunkName: "dsp-deeplink" */ '../dsp-deeplink'),
  ({ DspDeeplink: Page }) => Page,
);

const DataClassificationDeeplink = PickPageLoader(
  async () => import(/* webpackChunkName: "data-classification-deeplink" */ '../data-classification-deeplink'),
  ({ DataClassificationDeeplink: Page }) => Page,
);

type RootRoutes = Record<
  string,
  {
    exact?: boolean;
    navigation: boolean;
    importer: React.ComponentType<any> & LoadableComponent;
    name?: string;
  }
>;

export const ROOT_ROUTES: RootRoutes = {
  '/product-request-workflow/*': {
    importer: RequestGate,
    navigation: false,
    name: RouteNames.TOP_ROUTE_SHADOW_IT_REQUEST_GATE,
  },
  '/atlassian-access': {
    importer: OrgPickerSwitcher,
    navigation: true,
    name: RouteNames.TOP_ROUTE_ORG_PICKER_ATLASSIAN_ACCESS,
  },
  '/atlassian-guard': {
    importer: OrgPickerSwitcher,
    navigation: true,
    name: RouteNames.TOP_ROUTE_ORG_PICKER_ATLASSIAN_GUARD,
  },
  '/atlassian-guard-premium': {
    importer: OrgPickerSwitcher,
    navigation: true,
    name: RouteNames.TOP_ROUTE_ORG_PICKER_ATLASSIAN_GUARD_PREMIUM,
  },
  '/claim': {
    importer: ClaimApp,
    navigation: false,
  },
  // Deeplink feature gate uses fg() inside page components (not here): module-level fg() runs before FeatureGatesInitialization.
  // exact: false so paths still match with query params (e.g. ?bifrost-override= for branch preview).
  '/dsp': {
    importer: DspDeeplink,
    navigation: true,
    exact: false,
  },
  '/data-security-policies': {
    importer: DspDeeplink,
    navigation: true,
    exact: false,
  },
  '/data-classification': {
    importer: DataClassificationDeeplink,
    navigation: true,
    exact: false,
  },
  '/rovo/o/:orgId': {
    importer: RovoSignupOrgPage,
    navigation: true,
  },
  '/o/create': (
    fg('use_frontend_feature_encoding_sdk_adminhub')
      ? !isFeatureEnabled('adminhub', 'adminhub-org-create')
      : isFeatureDisabled({ featureKey: FEATURE_KEY_ADMINHUB_ORG_CREATE })
  )
    ? {
        importer: Error404Page,
        navigation: true,
      }
    : {
        importer: CreateOrganizationPage,
        navigation: false,
      },
  // Single catch-all for org; /o/:orgId/idp/:idpId is handled by org-routes path="idp/:idpId/*"
  // so the index route only runs for the true org root (/o/:orgId).
  '/o/:orgId/*': {
    importer: OrgRoutes,
    navigation: true,
  },
  // Use single catch-all for site so that pathname "remaining" after /s/:cloudId is non-empty for JSM paths
  // (e.g. .../idp/basicDirectoryId). Otherwise an index route in SiteRoutes would match and redirect to /users.
  '/s/:cloudId/*': {
    importer: SiteRoutes,
    navigation: true,
  },
  '/': {
    importer: Dashboard,
    navigation: true,
    exact: true,
    name: RouteNames.TOP_ROUTE_DASHBOARD,
  },
  '/cloud-first-create-a-migration/:orgId?': {
    importer: OrgOptIn,
    navigation: true,
  },
  '*': {
    importer: Error404Page,
    navigation: true,
  },
};
